import { Box, CircularProgress } from '@mui/material';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sendNotifications } from '../../../../apis/sendNotification';
import { sendReceipt } from '../../../../apis/sendReceipt';
import ALIGN_LEFT_ICON from '../../../../assets/svg/align-left-icon.svg';
import Button from '../../../../components/Button/Button';
import Modal from '../../../../components/Modal/Modal';
import Toast from '../../../../components/Toast/Toast';
import db_config from '../../../../configs/db_config';
import { LANG_TEXT } from '../../../../constants/language';
import { DELIVERY_FEE } from '../../../../consts/language';
import { useAuthContext } from '../../../../context/AuthContext/AuthContext';
import { useOrderContext } from '../../../../context/OrderContext';
import { useOrderStadiumContext } from '../../../../context/OrderStadiumContext';
import { useSettingsContext } from '../../../../context/SettingsContext';
import useCreateRefund from '../../../../hooks/useCreateRefund';
import usePermission from '../../../../hooks/usePermission';
import usePrinter from '../../../../hooks/usePrinter';
import { DamagedItemStatus, OrderItemStatus } from '../../../../interfaces/index.types';
import { db } from '../../../../libs/firebase';
import { doc, getDoc } from '../../../../libs/firestore-wrapper';
import { IOrders } from '../../order.types';
import { buttonBackStyle, buttonStyle, textButton, useStyles } from '../DrawerOrderItem/styles';

interface PartialCancellationProps {
  setCancellationOrder: React.Dispatch<React.SetStateAction<boolean>>;
  setPartialCancellation: React.Dispatch<React.SetStateAction<boolean>>;
  toggleDrawer?: ((open: boolean) => void) | undefined;
  updatePromotionOrderStatus?: ((orderId: string, status: string) => Promise<void>) | undefined;
  updateRegularOrderStatus?: ((orderId: string, status: string) => Promise<void>) | undefined;
  updateOrderStatus?: ((orderId: string, status: string, updatedFoods?: IOrders['foods'], allowRegression?: boolean) => Promise<void>) | undefined;
  order?: IOrders;
  foodQuantities: Record<string, number>;
  restaurantId?: string;
}

const PartialCancellation: React.FC<PartialCancellationProps> = ({
  setCancellationOrder,
  setPartialCancellation,
  toggleDrawer,
  updatePromotionOrderStatus,
  updateRegularOrderStatus,
  updateOrderStatus,
  order,
  foodQuantities,
  restaurantId,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [openModalPartial, setOpenModalPartial] = useState<boolean>(false);
  const handleOpenModalPartial = () => setOpenModalPartial(true);
  const handleCloseModalPartial = () => setOpenModalPartial(false);
  const { mutateAsync } = useCreateRefund();
  const { stadiumData } = useSettingsContext();
  const { userInfo } = useAuthContext();
  const toast = useRef<{ type: 'error' | 'success'; content: string }>({
    type: 'success',
    content: '',
  });
  const [openToast, setOpenToast] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { addToQueue } = usePrinter();

  const { updateOrderStatusDamaged, allOrders: resORders } = useOrderContext();
  const { allOrders: stadiumOrders } = useOrderStadiumContext();
  const { showForSuperAdmin, showForAdmin } = usePermission();

  const allOrders = useMemo(() => {
    if (showForSuperAdmin || showForAdmin) {
      return stadiumOrders;
    }

    return resORders;
  }, [resORders, stadiumOrders, showForSuperAdmin, showForAdmin]);

  const calculateUpdatedFoods = (order: IOrders, foodQuantities: Record<string, number>) => {
    return (
      order.all_foods?.map((food) => {
        if (restaurantId && food.restaurantId !== restaurantId) {
          return food;
        }
        const newQuantity = foodQuantities[food.item_food_id] ?? food.item_quantity;

        return {
          ...food,
          item_deleted_quantity: food.item_deleted_quantity !== undefined ? food.item_deleted_quantity + newQuantity : newQuantity,
        };
      }) || []
    );
  };

  const updateOrderAndNotify = async (orderId: string, updatedFoods: any[], newStatus: OrderItemStatus, order: IOrders) => {
    await updateOrderStatus?.(orderId, newStatus, updatedFoods, true);

    if (order.promotion_foods?.length) {
      order.type === 'promotion' ? await updatePromotionOrderStatus?.(orderId, newStatus) : await updateRegularOrderStatus?.(orderId, newStatus);
    }

    if (order.rider_id) {
      const parent_id = order?.parent_id ?? order.id;
      await sendNotifications(order.rider_id, { id: parent_id, status: newStatus });
    }
  };

  const handleConfirmPartialCancellation = async (order: IOrders) => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      if (!order.is_damaged) {
        const foodsToCalculate = restaurantId ? order.foods.filter((food) => food.restaurantId === restaurantId) : order.foods;
        const totalInitialQuantity = foodsToCalculate.reduce((acc, food) => acc + food.item_quantity, 0);
        const updatedFoods = calculateUpdatedFoods(order, foodQuantities);
        const totalUpdatedQuantity = updatedFoods
          .filter((food) => !restaurantId || food.restaurantId === restaurantId)
          .reduce((acc, food) => acc + (food.item_deleted_quantity || 0), 0);
        const total = updatedFoods
          .filter((food) => !restaurantId || food.restaurantId === restaurantId)
          .reduce((acc, food) => acc + (foodQuantities[food.item_food_id] ?? food.item_quantity) * food.item_price, 0);
        const snapshot = await getDoc(doc(db, db_config.DB_STADIUM_BILLS, order.id));
        const billData = snapshot.data()?.data?.object;
        if (!billData) throw new Error('Bill data not found');

        const newStatus = totalInitialQuantity - totalUpdatedQuantity === 0 ? OrderItemStatus.CANCELLED : OrderItemStatus.PREPARING;

        const isFullOrderCancelled = (() => {
          if (!order.multi_orders || order.multi_orders.length === 0) {
            return newStatus === OrderItemStatus.CANCELLED;
          }
          const otherOrders = order.multi_orders.filter((o) => o.restaurant_id !== restaurantId);
          const allOthersCancelled = otherOrders.every((o) => o.status === OrderItemStatus.CANCELLED || o.status === DamagedItemStatus.DAMAGED_CANCELLED);
          return newStatus === OrderItemStatus.CANCELLED && allOthersCancelled;
        })();

        await mutateAsync({
          chargeId: billData.id,
          amount: total + (isFullOrderCancelled ? DELIVERY_FEE : 0),
          reason: 'cancellation',
          stadiumID: stadiumData.id || userInfo?.stadium_id || '',
        });

        await updateOrderAndNotify(order.id, updatedFoods, newStatus, order);
        try {
          await sendReceipt(order.id, 'cancel');
        } catch (e) {}
        if (newStatus === OrderItemStatus.PREPARING) {
          addToQueue({
            data: { ...order, foods: updatedFoods },
            type: 'automatic',
          });
        }
      } else if (order.parent_id) {
        const orderOrigin = allOrders.find((i) => i.id === order.parent_id);
        if (orderOrigin) {
          const foodsToCalculateOrigin = restaurantId ? orderOrigin.all_foods?.filter((food) => food.restaurantId === restaurantId) || [] : orderOrigin.all_foods || [];
          const totalInitialQuantity = foodsToCalculateOrigin.reduce((acc, food) => acc + food.item_quantity, 0);
          const updatedFoods = calculateUpdatedFoods(orderOrigin, foodQuantities);
          const totalUpdatedQuantity = updatedFoods
            .filter((food) => !restaurantId || food.restaurantId === restaurantId)
            .reduce((acc, food) => acc + (food.item_deleted_quantity || 0), 0);
          const total = updatedFoods
            .filter((food) => !restaurantId || food.restaurantId === restaurantId)
            .reduce((acc, food) => acc + (foodQuantities[food.item_food_id] ?? food.item_quantity) * food.item_price, 0);
          const snapshot = await getDoc(doc(db, db_config.DB_STADIUM_BILLS, orderOrigin.id));
          const billData = snapshot.data()?.data?.object;
          if (!billData) throw new Error('Bill data not found');

          const newStatus = totalInitialQuantity - totalUpdatedQuantity === 0 ? OrderItemStatus.CANCELLED : OrderItemStatus.DAMAGED_PREPARING;

          const foodsToCalculateDamaged = restaurantId ? order.foods.filter((food) => food.restaurantId === restaurantId) : order.foods;
          const totalInitialQuantityDamaged = foodsToCalculateDamaged.reduce((acc, food) => acc + food.item_quantity, 0);
          const updatedFoodsDamaged = calculateUpdatedFoods(order, foodQuantities);
          const totalUpdatedQuantityDamaged = updatedFoodsDamaged
            .filter((food) => !restaurantId || food.restaurantId === restaurantId)
            .reduce((acc, food) => acc + (food.item_deleted_quantity || 0), 0);
          const newStatusDamaged =
            totalInitialQuantityDamaged - totalUpdatedQuantityDamaged === 0 ? DamagedItemStatus.DAMAGED_CANCELLED : DamagedItemStatus.DAMAGED_PREPARING;

          const isFullOrderCancelledDamaged = (() => {
            if (!orderOrigin.multi_orders || orderOrigin.multi_orders.length === 0) {
              return newStatus === OrderItemStatus.CANCELLED;
            }
            const otherOrders = orderOrigin.multi_orders.filter((o) => o.restaurant_id !== restaurantId);
            const allOthersCancelled = otherOrders.every((o) => o.status === OrderItemStatus.CANCELLED || o.status === DamagedItemStatus.DAMAGED_CANCELLED);
            return newStatus === OrderItemStatus.CANCELLED && allOthersCancelled;
          })();

          await mutateAsync({
            chargeId: billData.id,
            amount: total + (isFullOrderCancelledDamaged ? DELIVERY_FEE : 0),
            reason: 'cancellation',
            stadiumID: stadiumData.id || userInfo?.stadium_id || '',
          });

          await updateOrderStatus?.(order.id, newStatusDamaged, undefined, true);

          if (newStatusDamaged === DamagedItemStatus.DAMAGED_CANCELLED) {
            updateOrderStatusDamaged(order.parent_id, DamagedItemStatus.DAMAGED_CANCELLED);
          }

          await updateOrderAndNotify(orderOrigin.id, updatedFoods, newStatus, order);
          try {
            await sendReceipt(orderOrigin.id, 'cancel');
          } catch (e) {}

          if (newStatus === OrderItemStatus.DAMAGED_PREPARING) {
            addToQueue({
              data: { ...orderOrigin, foods: updatedFoods },
              type: 'automatic',
            });
          }
        }
      }

      toggleDrawer!(false);
      toast.current = { content: t(LANG_TEXT.UPDATE_SUCCESS_MESSAGE), type: 'success' };
      setOpenToast(true);
      // navigate(`/dashboard`);
    } catch (error) {
      toast.current = { content: t(LANG_TEXT.UPDATE_FAILED_MESSAGE), type: 'error' };
      setOpenToast(true);
      console.error('Update order status failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box className={classes.BoxButton}>
      {isLoading && (
        <Box className={classes.LoadingOverlay}>
          <CircularProgress color="inherit" />
        </Box>
      )}
      <Button
        id="close_button"
        fullWidth={false}
        boxStyle={{ width: 'auto' }}
        buttonStyle={buttonBackStyle}
        onClick={() => {
          setCancellationOrder(false);
          setPartialCancellation(false);
        }}
        startIcon={<img src={ALIGN_LEFT_ICON} height={20} width={20} alt="ALIGN_LEFT_ICON" />}
      />
      <Button
        id="CONFIRM_PARTIAL"
        fullWidth={false}
        title={t(LANG_TEXT.CONFIRM_PARTIAL)}
        buttonStyle={{
          ...buttonStyle,
          background: 'var(--Primary-50, #F2FBF3) !important',
        }}
        sxTextStyle={{
          ...textButton,
          color: 'var(--Primary-700, #296C32) !important',
        }}
        onClick={() => handleOpenModalPartial()}
      />
      <Modal
        open={openModalPartial}
        handleClose={handleCloseModalPartial}
        handleSubmit={() => handleConfirmPartialCancellation(order!)}
        title={t(LANG_TEXT.PARTIAL_CANCELLATION_TITLE)}
        description={t(LANG_TEXT.PARTIAL_CANCELLATION_DESCRIPTION)}
      />
      <Toast
        content={toast.current?.content}
        variant={toast.current?.type}
        open={openToast}
        onClose={() => {
          setOpenToast(false);
        }}
      />
    </Box>
  );
};

export default PartialCancellation;
